import { useEffect, useRef } from 'react';

import type { Customer } from '@/features/intelligence-dashboard/customer.schema';

import { fetchReferrals, toStoreReferral } from './referrals-api';
import { useReferralsStore } from './referrals-store';

/**
 * One-shot API → store referral sync (SCRUM-343). Call after the customer
 * query settles (branch resolution needs the live customers). Server rows
 * merge ON TOP of the client seeds — seeds keep the demo rich before any
 * server rows exist; API rows win on id collision. Silent on failure
 * (offline preview keeps the seeded experience; workflow actions remain
 * client-side until SCRUM-341).
 */
export function useReferralsSync(customers: readonly Customer[], settled: boolean): void {
  const mergeApiReferrals = useReferralsStore((s) => s.mergeApiReferrals);
  const syncedRef = useRef(false);

  useEffect(() => {
    if (!settled || syncedRef.current) return;
    syncedRef.current = true;
    fetchReferrals()
      .then((rows) => {
        mergeApiReferrals(rows.map((r) => toStoreReferral(r, customers)));
      })
      .catch(() => {
        // API unreachable (offline preview) — the seeded store stands alone.
      });
  }, [settled, customers, mergeApiReferrals]);
}
